import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, MapPin, CalendarDays, Flag, Home as HomeIcon, History, ChevronDown, Trophy, Maximize2, Sparkles, Images, ChevronLeft, ChevronRight, UserPlus } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { NotifyCTA } from "@/components/NotifyCTA";
import { TournamentSignup } from "@/components/TournamentSignup";
import { Lightbox } from "@/components/ZoomableImage";
import { useSpotsLabel } from "@/lib/useSpotsLabel";
import { RichText } from "@/components/RichText";
import { LiveNowCard } from "@/components/LiveNowCard";
import { TournamentPlayerList } from "@/components/TournamentPlayerList";

const NEW_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
function isNewEvent(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  return !Number.isNaN(t) && Date.now() - t < NEW_WINDOW_MS;
}

function EventCard({ ev, allowSignup, showNew, players, onRegistered }) {
  const [open, setOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const gallery = (ev.gallery || []).filter(Boolean);
  const allPics = [ev.image, ...gallery].filter(Boolean); // poster first, then event photos
  const hasPhotos = gallery.length > 0;
  const hasPlayers = (players || []).length > 0;
  // Typed list only for events without online sign-up.
  const typedList = !hasPlayers && !ev.signups_enabled ? ev.signup_list : "";
  const hasDetails = ev.description || ev.link || hasPlayers || typedList || hasPhotos;
  const fresh = showNew && isNewEvent(ev.created_at);
  const canSignup = allowSignup && ev.signups_enabled;
  const signupFromViewer = (e) => { e.stopPropagation(); setLightboxIdx(null); setSignupOpen(true); };
  const viewerOpen = lightboxIdx !== null;
  // Refresh whenever the viewer or sign-up form opens/closes so the count stays current.
  const spotsLabel = useSpotsLabel(ev.id, canSignup, `${viewerOpen}-${signupOpen}`);

  const openLightbox = (i) => setLightboxIdx(i);
  const closeLightbox = () => setLightboxIdx(null);
  const go = (dir) => setLightboxIdx((i) => (i + dir + allPics.length) % allPics.length);
  const nav = (e, dir) => { e.stopPropagation(); go(dir); };
  // galleryStart maps a gallery index -> index within allPics
  const galleryStart = ev.image ? 1 : 0;

  return (
    <div data-testid={`event-card-${ev.id}`} className="relative bg-white rounded-lg border border-[#E5E7EB] shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col">
      {ev.image && (
        <button
          type="button"
          onClick={() => openLightbox(0)}
          data-testid={`event-image-${ev.id}`}
          aria-label={`View ${ev.title} full screen`}
          className="group relative block bg-[#0B0B0C] cursor-zoom-in"
        >
          {fresh && (
            <span data-testid={`event-new-badge-${ev.id}`} className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 rounded-full bg-[#E11D2A] text-white text-xs font-bold uppercase tracking-wide px-2.5 py-1 shadow-md animate-pulse">
              <Sparkles className="h-3 w-3" /> New
            </span>
          )}
          <img src={fileUrl(ev.image)} alt={ev.title} loading="lazy" decoding="async" className="w-full max-h-[480px] object-contain mx-auto transition-transform duration-300 group-hover:scale-[1.02]" />
          {hasPhotos && (
            <span data-testid={`event-photos-flag-${ev.id}`} className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-md bg-black/65 text-white text-xs font-semibold px-2 py-1">
              <Images className="h-3.5 w-3.5" /> {allPics.length} photos
            </span>
          )}
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/55 text-white text-xs px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Maximize2 className="h-3 w-3" /> Enlarge
          </span>
        </button>
      )}
      <div className="p-5 flex flex-col flex-1">
        {fresh && !ev.image && (
          <span data-testid={`event-new-badge-${ev.id}`} className="self-start mb-2 inline-flex items-center gap-1 rounded-full bg-[#E11D2A] text-white text-xs font-bold uppercase tracking-wide px-2.5 py-1 shadow-sm animate-pulse">
            <Sparkles className="h-3 w-3" /> New
          </span>
        )}
        <h3 className="font-heading font-bold text-lg leading-snug">
          {ev.slug ? (
            <Link to={`/tournaments/${ev.slug}`} data-testid={`event-page-link-${ev.id}`} className="hover:text-[#1E63C4] transition-colors">{ev.title}</Link>
          ) : ev.title}
        </h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-[#555555]">
          {ev.date && (
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-4 w-4 text-[#1E63C4]" /> {ev.date}
            </span>
          )}
          {ev.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-4 w-4 text-[#1E63C4]" /> {ev.location}
            </span>
          )}
          {hasPhotos && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid={`event-photos-pill-${ev.id}`}
              className="inline-flex items-center gap-1 text-[#1E63C4] font-semibold hover:underline"
            >
              <Images className="h-4 w-4" /> {gallery.length} photo{gallery.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
        {ev.result && (
          <div className="mt-3 text-sm font-semibold text-[#1E63C4]">
            <span className="inline-flex items-center gap-1.5"><Trophy className="h-4 w-4 text-[#E11D2A]" /> Winner:</span>
            <span className="block whitespace-pre-line font-normal text-[#333333] mt-0.5">{ev.result}</span>
          </div>
        )}
        {hasDetails && (
          <>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              data-testid={`event-toggle-${ev.id}`}
              className="mt-3 inline-flex items-center gap-1 text-[#1E63C4] font-semibold text-sm self-start hover:gap-1.5 transition-all"
            >
              {open ? "Hide details" : "Show details"} <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="mt-3">
                {ev.description && <RichText html={ev.description} className="text-sm" testid={`event-desc-${ev.id}`} />}
                {hasPhotos && (
                  <div className="mt-3" data-testid={`event-gallery-${ev.id}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#1E63C4] mb-2 inline-flex items-center gap-1"><Images className="h-3.5 w-3.5" /> Event Photos</p>
                    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                      {gallery.map((g, gi) => (
                        <button
                          key={gi}
                          type="button"
                          onClick={() => openLightbox(galleryStart + gi)}
                          data-testid={`event-gallery-thumb-${ev.id}-${gi}`}
                          className="aspect-square rounded-md overflow-hidden border border-[#E5E7EB] hover:ring-2 hover:ring-[#1E63C4] transition-all"
                          aria-label={`View photo ${gi + 1}`}
                        >
                          <img src={fileUrl(g)} alt={`${ev.title} photo ${gi + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {hasPlayers && (
                  <div className="mt-3 rounded-md border border-[#E5E7EB] bg-[#F9FAF8] p-3">
                    <TournamentPlayerList groups={players} testid={`event-players-${ev.id}`} />
                  </div>
                )}
                {typedList && (
                  <div className="mt-3 rounded-md border border-[#E5E7EB] bg-[#F9FAF8] p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#1E63C4] mb-1">Player Sign-Up List</p>
                    <p className="text-sm text-[#333333] leading-relaxed whitespace-pre-line" data-testid={`event-signup-${ev.id}`}>{typedList}</p>
                  </div>
                )}
                {ev.link && (
                  <a
                    href={ev.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`event-link-${ev.id}`}
                    className="inline-flex items-center gap-1.5 mt-3 text-[#1E63C4] font-semibold text-sm hover:gap-2.5 transition-all"
                  >
                    Event website <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            )}
          </>
        )}
        {canSignup && (
          <TournamentSignup eventId={ev.id} title={ev.title} open={signupOpen} onOpenChange={setSignupOpen} badge={spotsLabel} divisions={ev.divisions || []} onRegistered={onRegistered} />
        )}
      </div>
      {lightboxIdx !== null && allPics.length > 0 && (
        <Lightbox
          key={lightboxIdx}
          src={fileUrl(allPics[lightboxIdx])}
          alt={allPics.length > 1 ? `${ev.title} ${lightboxIdx + 1}` : ev.title}
          onClose={closeLightbox}
          onNavigate={allPics.length > 1 ? go : undefined}
          testId={`event-lightbox-${ev.id}`}
        >
          {(allPics.length > 1 || canSignup) && (
            <>
          {allPics.length > 1 && (
            <>
              {/* Side arrows on larger screens; on phones they sit beside the counter so the flyer isn't covered. */}
              <button
                onClick={(e) => nav(e, -1)}
                data-testid={`event-lightbox-prev-${ev.id}`}
                aria-label="Previous photo"
                className="hidden sm:block absolute left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 z-10"
              >
                <ChevronLeft className="h-7 w-7" />
              </button>
              <button
                onClick={(e) => nav(e, 1)}
                data-testid={`event-lightbox-next-${ev.id}`}
                aria-label="Next photo"
                className="hidden sm:block absolute right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2 z-10"
              >
                <ChevronRight className="h-7 w-7" />
              </button>
              <div className="flex items-center gap-5 mt-3" onClick={(e) => e.stopPropagation()}>
                <button onClick={(e) => nav(e, -1)} data-testid={`event-lightbox-prev-mobile-${ev.id}`} aria-label="Previous photo" className="sm:hidden text-white/80 hover:text-white bg-white/10 rounded-full p-1.5">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <p className="text-white/70 text-sm" data-testid={`event-lightbox-counter-${ev.id}`}>{lightboxIdx + 1} / {allPics.length}</p>
                <button onClick={(e) => nav(e, 1)} data-testid={`event-lightbox-next-mobile-${ev.id}`} aria-label="Next photo" className="sm:hidden text-white/80 hover:text-white bg-white/10 rounded-full p-1.5">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="flex gap-2 mt-3 max-w-[92vw] overflow-x-auto pb-1" onClick={(e) => e.stopPropagation()}>
                {allPics.map((p, pi) => (
                  <button
                    key={pi}
                    onClick={() => setLightboxIdx(pi)}
                    data-testid={`event-lightbox-thumb-${ev.id}-${pi}`}
                    className={`h-14 w-14 shrink-0 rounded-md overflow-hidden border-2 transition-all ${pi === lightboxIdx ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
                    aria-label={`Go to photo ${pi + 1}`}
                  >
                    <img src={fileUrl(p)} alt={`thumb ${pi + 1}`} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </>
          )}
          {canSignup && (
            <button
              onClick={signupFromViewer}
              data-testid={`event-lightbox-signup-${ev.id}`}
              className="mt-4 max-w-[92vw] inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 bg-[#E11D2A] text-white rounded-full px-6 py-3 text-sm font-bold shadow-lg hover:bg-[#c41320] transition-colors"
            >
              <span className="inline-flex items-center gap-2"><UserPlus className="h-4 w-4" /> Sign up for this tournament</span>
              {spotsLabel && (
                <span data-testid={`event-lightbox-spots-${ev.id}`} className="rounded-full bg-white text-[#E11D2A] px-2.5 py-0.5 text-xs font-extrabold">
                  {spotsLabel}
                </span>
              )}
            </button>
          )}
            </>
          )}
        </Lightbox>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, accent, events, empty, allowSignup, showNew, players, onRegistered }) {
  return (
    <section className="mb-14" data-testid={`tournament-section-${title.split(" ")[1] || title}`}>
      <div className="flex items-center gap-2 mb-6">
        <span className={`flex h-9 w-9 items-center justify-center rounded-full ${accent}`}>
          <Icon className="h-5 w-5" />
        </span>
        <h2 className="font-heading text-2xl font-bold">{title}</h2>
      </div>
      {events.length === 0 ? (
        <p className="text-[#555555] text-sm">{empty}</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {events.map((e) => (
            <EventCard key={e.id} ev={e} allowSignup={allowSignup} showNew={showNew} players={players[e.id]} onRegistered={onRegistered} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function Tournaments() {
  const [site, setSite] = useState(null);
  const [players, setPlayers] = useState({});
  const loadPlayers = () => api.get("/tournaments/players").then((r) => setPlayers(r.data || {})).catch(() => {});
  useEffect(() => {
    api.get("/site").then((r) => setSite(r.data)).catch(() => {});
    loadPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const lists = { players, onRegistered: loadPlayers };

  return (
    <Layout>
      <Seo title="Pool Tournaments in Trenton & Eastern Ontario | Quinte BCA" description="View upcoming Quinte BCA pool tournaments, registration information, Fargo limits, live tournament tracking, brackets, results and past events." path="/tournaments" />
      <PageHeader eyebrow="Compete" title="Tournaments" subtitle={site?.tournaments_intro} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-8"><LiveNowCard /></div>
        <Section
          icon={HomeIcon}
          title="Upcoming — Local"
          accent="bg-[#1E63C4] text-white"
          events={site?.upcoming_local || []}
          empty="No local tournaments scheduled right now."
          allowSignup
          showNew
          {...lists}
        />
        <Section
          icon={Flag}
          title="Upcoming — National"
          accent="bg-[#E11D2A] text-white"
          events={site?.upcoming_national || []}
          empty="No national tournaments listed right now."
          showNew
          {...lists}
        />
        <Section
          icon={History}
          title="Previous Results (Local)"
          accent="bg-gray-700 text-white"
          events={site?.previous_results || []}
          empty="Past local results will appear here."
          {...lists}
        />
      </div>
      <NotifyCTA title="Get notified about new tournaments" text="Be the first to know when we add a local or national event, or post results. We'll email you — no account needed." />
    </Layout>
  );
}
